import { useDraggable } from "@dnd-kit/core";
import { Check, CornerDownRight } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ClaimButton } from "@/components/messages/claim-button";
import { TaskDueChip, TaskSourceTag } from "@/components/messages/task-card";
import { getAvatarColorClasses } from "@/lib/messages/avatar-color";
import { getThreadDisplayName, getThreadInitials } from "@/lib/messages/messages-helpers";
import { findGroupById, getGroupColor, getThreadGroupId } from "@/lib/thread-groups";
import { getAuthUser } from "@/lib/api/token-store";
import { cn } from "@/lib/utils";

function ownerInitials(name) {
  const tokens = String(name || "")
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0][0]?.toUpperCase() || "?";
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
}

/**
 * One compact task-board card (the swim-lane kanban): a done checkbox, the title, the
 * linked-thread chip, source + due chips, and a footer — a Claim quick-action while unclaimed,
 * the owner avatar otherwise. The whole card body is the drag handle (assign by dragging into a
 * lane, snooze/complete by dragging across columns); the inner controls stop pointer events so
 * a tap on them never starts a drag.
 */
export function TaskBoardCard({
  task,
  nowMs = Date.now(),
  groups = [],
  onToggle,
  onClaim,
  onOpenThread,
  isDragging = false,
  dragHandleProps,
}) {
  const me = String(getAuthUser()?.id || "").trim();
  const done = task.status === "done";
  const thread = task.thread;
  const threadId = String(task.threadId || "").trim();
  const { label: threadTitle } = thread ? getThreadDisplayName(thread) : { label: "" };
  const groupId = thread ? getThreadGroupId(thread) : "";
  const group = groupId ? findGroupById(groups, groupId) : null;
  const groupColor = group ? getGroupColor(group.colorId) : null;
  const unclaimed = !done && !String(task.assigneeId || "").trim();
  const assigneeIsMe = Boolean(task.assigneeId && me && task.assigneeId === me);
  const assigneeName = String(task.assignee?.displayName || "").trim();

  const stop = (e) => e.stopPropagation();

  return (
    <div
      {...dragHandleProps}
      className={cn(
        "cursor-grab rounded-lg border border-border bg-card p-2.5 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isDragging ? "opacity-50 ring-2 ring-indigo-400/60" : "hover:shadow-md",
        done ? "opacity-70" : "",
      )}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          role="checkbox"
          aria-checked={done}
          aria-label={done ? `Reopen task: ${task.title}` : `Complete task: ${task.title}`}
          onPointerDown={stop}
          onClick={(e) => {
            stop(e);
            onToggle?.(task.id, !done);
          }}
          className={cn(
            "mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            done
              ? "bg-emerald-500 text-white"
              : "border-2 border-muted-foreground/40 hover:border-emerald-500",
          )}
        >
          {done ? <Check className="h-2.5 w-2.5" strokeWidth={3} aria-hidden /> : null}
        </button>
        <p
          className={cn(
            "min-w-0 flex-1 text-[12.5px] leading-snug",
            done ? "text-muted-foreground line-through" : "font-semibold text-foreground",
          )}
        >
          {task.title}
        </p>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-[26px]">
        {thread ? (
          <button
            type="button"
            onPointerDown={stop}
            onClick={(e) => {
              stop(e);
              onOpenThread?.(threadId);
            }}
            title={`Open conversation with ${threadTitle}`}
            className="inline-flex min-w-0 items-center gap-1 rounded text-[10px] text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CornerDownRight className="h-2.5 w-2.5 shrink-0" strokeWidth={2.25} aria-hidden />
            <Avatar className="h-3.5 w-3.5 shrink-0">
              <AvatarImage src={thread?.avatarUrl} />
              <AvatarFallback
                className={cn("text-[7px] font-bold", getAvatarColorClasses(threadTitle || threadId))}
              >
                {getThreadInitials(threadTitle)}
              </AvatarFallback>
            </Avatar>
            <span className="max-w-[120px] truncate font-medium">{threadTitle}</span>
            {group ? (
              <span className={cn("h-1 w-1 shrink-0 rounded-full", groupColor?.dotClassName)} aria-hidden />
            ) : null}
          </button>
        ) : null}
        <TaskSourceTag task={task} />
        <TaskDueChip task={task} nowMs={nowMs} />
      </div>

      {unclaimed && onClaim ? (
        <div className="mt-2 pl-[26px]" onPointerDown={stop}>
          <ClaimButton
            onClick={() => onClaim(task.id)}
            className="px-2 py-1 text-[10.5px] font-bold"
            title="Claim this task — it's yours"
          />
        </div>
      ) : !done && (assigneeName || assigneeIsMe) ? (
        <div className="mt-2 flex items-center gap-1.5 pl-[26px]">
          <span
            className={cn(
              "flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-bold leading-none",
              assigneeIsMe ? "bg-indigo-600 text-white dark:bg-indigo-500" : "bg-muted text-muted-foreground",
            )}
            aria-hidden
          >
            {ownerInitials(assigneeIsMe && !assigneeName ? "Me" : assigneeName)}
          </span>
          <span className="truncate text-[10px] font-medium text-muted-foreground">
            {assigneeIsMe ? "You" : assigneeName}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** Draggable wrapper: the card can be dragged into any lane × column cell. */
export function DraggableTaskCard(props) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: String(props.task?.id || ""),
  });
  return (
    <div ref={setNodeRef}>
      <TaskBoardCard {...props} isDragging={isDragging} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}
